import { ResultRecord } from "@rsm-hcd/javascript-core";
import { ImageAnnotationRecord } from "models/view-models/block-configurations/image-annotation-record";
import { useState } from "react";
import { useLocalization } from "utilities/hooks/use-localization";
import ImageAnnotationService, {
    ImageAnnotationCreateParams,
} from "utilities/services/block-configurations/image-annotation-service";
import { ToastManager } from "utilities/toast/toast-manager";

const IMAGE_BLOCK_FORBIDDEN_KEY =
    "ImageAnnotationsController_ImageBlock_Forbidden";

const useImageAnnotations = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { create } = ImageAnnotationService.useCreate();
    const { update } = ImageAnnotationService.useUpdate();
    const { delete: deleteAnnotationApi } = ImageAnnotationService.useDelete();

    const { t } = useLocalization();

    // There was an issue creating this image annotation.
    const baseCreateErrorMessage = t("errors-actionResource", {
        action: t("creating"),
        resource: t("imageAnnotation").toLowerCase(),
    });

    // There was an issue updating this image annotation.
    const baseUpdateErrorMessage = t("errors-actionResource", {
        action: t("updating"),
        resource: t("imageAnnotation").toLowerCase(),
    });

    // There was an issue deleting this image annotation.
    const baseDeleteErrorMessage = t("errors-actionResource", {
        action: t("deleting"),
        resource: t("imageAnnotation").toLowerCase(),
    });

    // You do not have permission to edit this page.
    const noAccessErrorMessage = t("page-access-denied", {
        action: t("edit").toLowerCase(),
    });

    const createImageAnnotation = async (
        params: ImageAnnotationCreateParams
    ) => {
        setIsLoading(true);

        try {
            const response = await create(params);

            if (response?.result == null || response.resultObject == null) {
                throw new Error(
                    `Failed to create image annotation for ImageBlock ID ${params.imageBlockId}`
                );
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            return response.resultObject;
        } catch (error) {
            let errorMessage = baseCreateErrorMessage;

            if (
                error instanceof ResultRecord &&
                error.hasErrorFor(IMAGE_BLOCK_FORBIDDEN_KEY)
            ) {
                errorMessage = noAccessErrorMessage;
            }

            ToastManager.error(errorMessage);

            console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const updateImageAnnotation = async (
        updatedImageAnnotation: ImageAnnotationRecord
    ) => {
        setIsLoading(true);

        try {
            const response = await update(updatedImageAnnotation, {
                id: updatedImageAnnotation.id!,
            });

            if (response?.result == null || response.resultObject == null) {
                throw new Error(
                    `Failed to update image annotation for ImageBlock ID ${updatedImageAnnotation.id}`
                );
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            return response.resultObject;
        } catch (error) {
            let errorMessage = baseUpdateErrorMessage;

            if (
                error instanceof ResultRecord &&
                error.hasErrorFor(IMAGE_BLOCK_FORBIDDEN_KEY)
            ) {
                errorMessage = noAccessErrorMessage;
            }

            ToastManager.error(errorMessage);

            console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const deleteImageAnnotation = async (
        annotationToRemove: ImageAnnotationRecord
    ) => {
        setIsLoading(true);

        try {
            const response = await deleteAnnotationApi(annotationToRemove.id!);
            return response.resultObject;
        } catch (error) {
            let errorMessage = baseDeleteErrorMessage;

            if (
                error instanceof ResultRecord &&
                error.hasErrorFor(IMAGE_BLOCK_FORBIDDEN_KEY)
            ) {
                errorMessage = noAccessErrorMessage;
            }

            ToastManager.error(errorMessage);

            console.error(error);
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        createImageAnnotation,
        updateImageAnnotation,
        deleteImageAnnotation,
        isLoading,
    };
};

export default useImageAnnotations;
